import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { paths } from '@/routes/paths';

/**
 * A permanent, unmissable reminder that you are not yourself.
 *
 * It renders above everything and is not dismissible, on purpose. The failure
 * this exists to prevent is an admin forgetting which account they are in and
 * taking an action -- sending a message, withdrawing an application -- that the
 * person whose account it is appears to have taken. The audit log records who
 * really did it; the banner is what stops it happening at all.
 *
 * Nothing here calls the server to stop. The impersonation token has no refresh
 * token and the admin's own session was never invalidated, so exiting is
 * putting back the tokens the client kept.
 */
export function ImpersonationBanner() {
  const { isImpersonating, impersonation, stopImpersonating, user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  if (!isImpersonating) return null;

  const actingAs = impersonation?.user?.full_name ?? user?.full_name ?? 'another user';

  async function exit() {
    await stopImpersonating();
    // Everything cached was fetched as somebody else. Dropping the lot is the
    // only safe move: a stale student's applications rendered inside the admin
    // console would be a data leak dressed up as a UI bug.
    queryClient.clear();
    navigate(paths.admin.overview, { replace: true });
  }

  return (
    <div
      role="status"
      className="sticky top-0 z-50 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-warning-500 text-night px-4 py-2 text-center text-sm font-medium"
    >
      <span>
        You are signed in as <strong>{actingAs}</strong>. Anything you do here is recorded against
        your own account.
      </span>
      <Button size="sm" variant="secondary" onClick={exit}>
        Return to my account
      </Button>
    </div>
  );
}
